import { hasValue } from 'Lib/Any';
import { pipe } from 'Lib/Hooks/Selectors';
import React from 'react';
import { useSelector } from 'react-redux';
import deepEquals from '../../../../../Lib/Object/deepEquals';
import { Checkbox, Field, NumericInput, RadioGroup, TextOutput } from '../../../../Core';
import { withInjectedProps } from '../../../../Core/InjectedPropsContext';
import { Grid, GridFieldSection, ListFieldSection, PageSection, SectionGroup } from '../../../../Layout';
import { IWorksheetComponent, WorksheetAssociatedTestSection } from '../../../Core';
import externalTestMethods from '../../../ExternalTestMethods';

let OversizeDeterminationSection: React.FC<IWorksheetComponent> = ({ createWorksheetSelector }) => {
  const importedOversizeId = useSelector(
    createWorksheetSelector!('Preparation.AssociatedOversizeDetermination.ImportedTest.Id')
  );
  const fineMoistureContentDetermined =
    useSelector(createWorksheetSelector!('Preparation.FineMoistureContentDetermined')) ?? false;
  const disableFineMoistureContentDetermined = useSelector(
    pipe(createWorksheetSelector!('Preparation.AssociatedOversizeDetermination.MeasuredTest'), hasValue)
  );

  const oversizeDeterminationResultsPath = hasValue(importedOversizeId)
    ? 'OversizeDeterminationResults'
    : 'AssociatedOversizeDetermination.ExternalTest.Results';

  const sieves = useSelector(
    createWorksheetSelector!(`Preparation.${oversizeDeterminationResultsPath}.Sieves`),
    deepEquals
  );

  const allSieves = useSelector(
    createWorksheetSelector!(`Preparation.${oversizeDeterminationResultsPath}.AllSieves`),
    deepEquals
  );

  const importedFineMoistureTestId = useSelector(
    createWorksheetSelector!('Preparation.AssociatedFineMoisture.ImportedTest.Id')
  );

  const oversizeSieveId = useSelector(createWorksheetSelector!('Preparation.ScreeningASTM.OversizeSieve.Id'));
  const compactionMethodDeterminedBy = useSelector(
    createWorksheetSelector!('Preparation.CompactionMethodDeterminedBy')
  );

  const isSieveAvailable = (sieveId: string) =>
    sieves && sieves.some((s: any) => s.SieveId === sieveId && s.IsAvailable);

  const sieveHasValue = (sieveId: string) =>
    sieves && sieves.some((s: any) => s.SieveId === sieveId && s.CumulativeDryMassRetainedPercent);

  const getRowHeaders = () => {
    if (!allSieves) return [];
    return allSieves.map((s: any) => s.Caption);
  };

  const getExternalGridData = () => {
    if (!allSieves) return [];
    return allSieves.map((sieveDef: any, index: number) => [
      getSieveCheckbox(sieveDef.Id, index),
      <Field
        input={<NumericInput />}
        disabled={!isSieveAvailable(sieveDef.Id)}
        name={`Preparation.AssociatedOversizeDetermination.ExternalTest.Results.Sieves[${index}].CumulativeDryMassRetainedPercent`}
        label="Cumulative Dry Mass Retained (%)"
      />,
    ]);
  };

  const getImportedGridData = () => {
    if (!allSieves) return [];
    return allSieves.map((sieveDef: any, index: number) => [
      <Field
        input={<TextOutput />}
        name={`Preparation.OversizeDeterminationResults.Sieves[${index}].CumulativeDryMassRetainedPercent`}
        label="Cumulative Dry Mass Retained (%)"
        blankReplacement="N/A"
      />,
    ]);
  };

  const getSieveCheckbox = (sieveId: any, index: number) => (
    <Field
      key={`sieveCheckbox-${sieveId}`}
      input={<Checkbox />}
      name={`Preparation.AssociatedOversizeDetermination.ExternalTest.Results.Sieves[${index}].IsAvailable`}
      confirm={({ value }) => value === false && sieveHasValue(sieveId)}
      confirmText="The dry mass retained percentage for the sieve will be removed."
      confirmButtonText="Remove Sieve"
      disabled={compactionMethodDeterminedBy === 'userdefined' && oversizeSieveId && oversizeSieveId === sieveId}
    />
  );

  const resultsSection = (
    <ListFieldSection>
      <Field input={<TextOutput />} label="Oversize Sieve" name="Preparation.ScreeningASTM.OversizeSieve.Caption" />
      <Field input={<TextOutput />} label="Oversize (%)" name="Preparation.ScreeningASTM.OversizePercent" />
    </ListFieldSection>
  );

  return (
    <PageSection heading="Oversize Determination">
      <WorksheetAssociatedTestSection
        label="Oversize proportion determined by:"
        name="Preparation.AssociatedOversizeDetermination"
        testType="Oversize Determination"
        associatedTestModel={{
          measured: true,
          imported: {
            importedTestResults: (
              <GridFieldSection>
                <Grid
                  cornerHeader="Sieve Size"
                  colHeaders={['Cumulative Dry Mass Retained (%)']}
                  colWidths={130}
                  rowHeaders={getRowHeaders()}
                  rowHeaderWidth={110}
                  data={getImportedGridData()}
                />
              </GridFieldSection>
            ),
            importModel: {
              Id: importedOversizeId,
            },
          },
          external: {
            permitOtherTestMethods: true,
            testMethods: [
              externalTestMethods.psdSieveAnalysis.ASTM_C136,
              externalTestMethods.psdSieveAnalysis.ASTM_D6913,
            ],
            externalTestResults: (
              <GridFieldSection>
                <Grid
                  cornerHeader="Sieve Size"
                  colHeaders={['', 'Cumulative Dry Mass Retained (%)']}
                  colWidths={(colIndex) => (colIndex === 0 ? 30 : 130)}
                  rowHeaders={getRowHeaders()}
                  rowHeaderWidth={110}
                  data={getExternalGridData()}
                />
              </GridFieldSection>
            ),
          },
        }}
      />
      <SectionGroup heading="Passing Material Water Content ">
        <ListFieldSection>
          <Field
            input={<RadioGroup />}
            label="Passing Material Water Content Determined"
            name="Preparation.FineMoistureContentDetermined"
            disabled={disableFineMoistureContentDetermined}
            options={[
              { label: 'Yes', value: true },
              { label: 'No', value: false },
            ]}
          />
        </ListFieldSection>
        <WorksheetAssociatedTestSection
          label="Passing Material Water Content Determined By:"
          visible={fineMoistureContentDetermined}
          name="Preparation.AssociatedFineMoisture"
          testType="Moisture Content"
          associatedTestModel={{
            measured: true,
            imported: {
              importedTestResults: (
                <ListFieldSection>
                  <Field
                    input={<TextOutput />}
                    label="Passing Material Water Content (%)"
                    name="Preparation.FineMoistureContent"
                    emph
                  />
                </ListFieldSection>
              ),
              importModel: {
                Id: importedFineMoistureTestId,
              },
            },
            external: {
              testMethods: [externalTestMethods.moistureContent.ASTM_D2216],
              permitOtherTestMethods: false,
              externalTestResults: (
                <ListFieldSection>
                  <Field
                    input={<NumericInput />}
                    label="Passing Material Water Content (%)"
                    name="Preparation.AssociatedFineMoisture.ExternalTest.Results.MoistureContentPercent"
                  />
                </ListFieldSection>
              ),
            },
          }}
        />
      </SectionGroup>
      {resultsSection}
    </PageSection>
  );
};

OversizeDeterminationSection = withInjectedProps(OversizeDeterminationSection);

export { OversizeDeterminationSection };
export default OversizeDeterminationSection;
